/**
 * Sending a sheet into a campaign as leads.
 *
 * The same job the import tab does, started from the file you are looking at
 * rather than from a fresh upload — and using the sheet as it currently reads,
 * so any changes made here are what land in the campaign.
 *
 * Mapping targets come from GET /meta/enums, the vocabulary the engine
 * actually implements, and from the organisation's lead properties: mapping
 * a column to one stores it as that custom field, under the property's key.
 * Only exact name matches are pre-filled (for a property, its key or label,
 * ignoring case, spaces and underscores): a wrong guess writes wrong data
 * into a campaign and is hard to spot afterwards, which is worse than making
 * someone choose.
 */
import { useEffect, useState } from 'react';
import {
  Alert, Button, Group, Modal, ScrollArea, Select, Stack, Switch, Table, Text,
} from '@mantine/core';
import { notifications } from '@mantine/notifications';
import {
  useCampaigns, useEntities, useEnums, useImportSheet, type SheetFilter,
} from '../api/queries';
import { useProperties } from '../api/properties';
import { formatNumber } from '../theme';
import { useVocab } from '../vocab';
import { ImportJobCard } from './ImportJobCard';
import { ImportPreflight, SAMPLE_ROWS } from './ImportPreflight';
import { propertyForColumn } from './properties/propertyModel';

const CUSTOM = '__custom__';

export function SheetImportModal({
  sheetId,
  fileName,
  columns,
  rowCount,
  filters,
  filterSummary,
  defaultSupplierId,
  opened,
  onClose,
}: {
  sheetId: string | null;
  fileName: string;
  columns: string[];
  /** Rows on screen: after the view's filters, not the size of the file. */
  rowCount: number;
  /**
   * The view's filters, sent with the import so the rows imported are the
   * rows counted above. Without them the whole file was imported while this
   * dialog showed the filtered count.
   */
  filters: SheetFilter[];
  /** The filters in words, for saying which rows go in. */
  filterSummary?: string;
  /** The file's own supplier, which the leads are credited to unless changed. */
  defaultSupplierId?: string | null;
  opened: boolean;
  onClose: () => void;
}) {
  const v = useVocab();
  const campaigns = useCampaigns();
  const enums = useEnums();
  const suppliers = useEntities('suppliers');
  const importSheet = useImportSheet(sheetId);
  const [campaignId, setCampaignId] = useState<string | null>(null);
  const [supplierId, setSupplierId] = useState<string | null>(null);
  const [mapping, setMapping] = useState<Record<string, string>>({});
  const [distribute, setDistribute] = useState(false);
  const [sampleFirst, setSampleFirst] = useState(false);
  /* Once started, this stays open on the running job rather than disappearing
     and leaving someone to go looking for what they just set off. */
  const [startedJobId, setStartedJobId] = useState<string | null>(null);

  const fields = enums.data?.import_fields ?? [];
  const props = useProperties('submission', { enabled: opened }).data?.properties ?? [];
  const standardValues = new Set(fields.map((f) => f.value));
  // A property sharing a standard field's name is that field, not a second target.
  const propTargets = props.filter((d) => !standardValues.has(d.key));

  useEffect(() => {
    if (opened) {
      setSupplierId(defaultSupplierId ?? null);
      setStartedJobId(null);
      setSampleFirst(false);
    }
  }, [opened]); // eslint-disable-line react-hooks/exhaustive-deps

  // Pre-fill only where the column name is exactly a known field, or a lead
  // property's key or label.
  useEffect(() => {
    if (!opened || fields.length === 0) return;
    // Match on the field's own value, case-insensitively, and map back to the
    // exact value the engine expects rather than to the lower-cased form.
    const byName = new Map(fields.map((f) => [f.value.toLowerCase(), f.value]));
    setMapping(
      Object.fromEntries(
        columns.map((c) => [c, byName.get(c.toLowerCase()) ?? propertyForColumn(c, propTargets)?.key ?? CUSTOM]),
      ),
    );
  }, [opened, columns, fields.length, propTargets.length]); // eslint-disable-line react-hooks/exhaustive-deps

  const campaign = campaigns.data?.campaigns.find((c) => c.id === campaignId);

  const columnMapping = Object.fromEntries(
    Object.entries(mapping).filter(([, field]) => field && field !== CUSTOM),
  );

  function start() {
    if (!campaignId) return;
    importSheet.mutate(
      {
        campaign_id: campaignId,
        column_mapping: columnMapping,
        distribute,
        filters,
        supplier_id: supplierId,
        row_limit: distribute && sampleFirst ? SAMPLE_ROWS : null,
      },
      {
        onSuccess: (r) => {
          setStartedJobId(r.job_id);
          notifications.show({
            color: 'teal',
            title: 'Import started',
            // There is no Import screen any more; the leads are the place to look.
            message: `The ${v.many('lead')} appear on the ${v.Many('lead')} page as they land. Filter by Import to see just these.`,
          });
        },
        onError: (e) =>
          notifications.show({
            color: 'red',
            title: 'Could not start the import',
            message: (e as Error).message,
          }),
      },
    );
  }

  return (
    <Modal opened={opened} onClose={onClose} title={`Import ${fileName}`} size="lg">
      {startedJobId ? (
        <Stack>
          <ImportJobCard jobId={startedJobId} title={fileName} />
          <Text size="xs" c="dimmed">
            {`You can close this. It carries on, and stays listed under the file's "Imported into" and on the Files page.`}
          </Text>
          <Group justify="flex-end">
            <Button variant="subtle" onClick={onClose}>Close</Button>
          </Group>
        </Stack>
      ) : (
      <Stack>
        <Select
          label={v.One('campaign')}
          description={`Where these rows become ${v.many('lead')}`}
          placeholder={`Choose ${v.a('campaign')}`}
          searchable
          data={(campaigns.data?.campaigns ?? []).map((c) => ({ value: c.id, label: c.name }))}
          value={campaignId}
          onChange={setCampaignId}
        />

        <Select
          label={v.One('supplier')}
          description={`Who sent this file. Their login will see these ${v.many('lead')}.`}
          placeholder={`No ${v.one('supplier')}`}
          searchable
          clearable
          data={(suppliers.data?.entities ?? [])
            .filter((s) => s.active)
            .map((s) => ({ value: s.id, label: s.name }))}
          value={supplierId}
          onChange={setSupplierId}
        />

        <Alert color="gray" variant="light">
          <Text size="sm">
            Imports <b>{formatNumber(rowCount)} rows</b> from <b>{fileName}</b>
            {filters.length > 0 && filterSummary ? <>, only those where {filterSummary}</> : null}.{' '}
            <b>The file is not changed.</b> The import is listed under the file&apos;s
            &ldquo;Imported into&rdquo; afterwards.
          </Text>
        </Alert>

        <div>
          <Text size="sm" fw={600}>Map columns</Text>
          <Text size="xs" c="dimmed">
            {`Anything left unmapped is stored as a custom field, searchable from the ${v.many('lead')} page. A column can also go into one of the fields you have described. Only exact name matches are pre-filled, because a wrong guess writes wrong data.`}
          </Text>
        </div>

        <ScrollArea.Autosize mah={320}>
          <Table>
            <Table.Thead>
              <Table.Tr>
                <Table.Th>Column</Table.Th>
                <Table.Th w={230}>Import as</Table.Th>
              </Table.Tr>
            </Table.Thead>
            <Table.Tbody>
              {columns.map((c) => (
                <Table.Tr key={c}>
                  <Table.Td><Text size="sm">{c}</Text></Table.Td>
                  <Table.Td>
                    <Select
                      size="xs"
                      searchable
                      data={[
                        { group: 'Store as', items: [{ value: CUSTOM, label: 'Custom field' }] },
                        {
                          group: `${v.One('lead')} fields`,
                          items: [
                            ...fields.map((f) => ({ value: f.value, label: f.label })),
                            ...propTargets.map((d) => ({ value: d.key, label: d.label })),
                          ],
                        },
                      ]}
                      value={mapping[c] ?? CUSTOM}
                      onChange={(value) => setMapping((m) => ({ ...m, [c]: value ?? CUSTOM }))}
                    />
                  </Table.Td>
                </Table.Tr>
              ))}
            </Table.Tbody>
          </Table>
        </ScrollArea.Autosize>

        <Switch
          label="Distribute after import"
          description={`Send each ${v.one('lead')} to ${v.many('buyer')} as it lands, rather than only storing it`}
          checked={distribute}
          onChange={(e) => setDistribute(e.currentTarget.checked)}
        />
        {distribute && campaign && (
          <Alert color="chartAmber" variant="light">
            <Text size="sm">
              {`These ${v.many('lead')} will be sent to ${campaign.name}'s ${v.many('buyer')} as they import.`}
            </Text>
          </Alert>
        )}
        {distribute && campaignId && (
          <ImportPreflight
            campaignId={campaignId}
            sheetId={sheetId}
            fileName={fileName}
            totalRows={rowCount}
            columnMapping={columnMapping}
            sampleFirst={sampleFirst}
            onSampleFirstChange={setSampleFirst}
          />
        )}

        <Group justify="flex-end">
          <Button variant="subtle" onClick={onClose}>Cancel</Button>
          <Button loading={importSheet.isPending} disabled={!campaignId} onClick={start}>
            {distribute && sampleFirst ? `Send the first ${SAMPLE_ROWS} rows` : 'Start import'}
          </Button>
        </Group>
      </Stack>
      )}
    </Modal>
  );
}
